import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import type { CompanySummary } from '../../../api/Navigation/moduleConfig';

/**
 * Searchable company picker of the Module Configuration page (CO-864). It replaced a native
 * `<select>` that listed names only: two tenants with the same name could not be told apart and a
 * tenant known by its id could not be found.
 *
 * WAI-ARIA combobox with a listbox popup. The trigger opens the popover; the search field inside is
 * the `combobox`, keeps DOM focus the whole time, and points at the highlighted option with
 * `aria-activedescendant`. Up / Down / Home / End move the highlight, Enter picks it, Escape closes
 * and returns focus to the trigger (Radix). A query matches a name anywhere, or an id written as
 * `#12` from its first digit.
 */

interface CompanyPickerProps {
  /** Id of the trigger button. The specs select the picker by it. */
  id: string;
  /** Id of the visible label, so the trigger is named "<label>, <company>". */
  labelId: string;
  companies: CompanySummary[];
  selectedId: number | null;
  /** Called with the company the operator picked. The page decides whether the switch happens. */
  onSelect: (companyId: number) => void;
  disabled?: boolean;
}

export function matchesCompany(company: CompanySummary, query: string): boolean {
  if (query === '') return true;
  return company.companyName.toLowerCase().includes(query) || `#${company.companyId}`.includes(query);
}

export default function CompanyPicker({ id, labelId, companies, selectedId, onSelect, disabled }: CompanyPickerProps) {
  const { t } = useTranslation();
  const listId = useId();
  const valueId = `${id}-value`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const selected = companies.find(company => company.companyId === selectedId) ?? null;
  const query = search.trim().toLowerCase();
  const matches = useMemo(() => companies.filter(company => matchesCompany(company, query)), [companies, query]);
  const active = matches[Math.min(activeIndex, matches.length - 1)] ?? null;
  const optionId = (companyId: number) => `${listId}-option-${companyId}`;
  const activeOptionId = active ? optionId(active.companyId) : undefined;

  const handleOpenChange = (next: boolean) => {
    if (next) {
      // Every opening starts from the whole list, with the current company highlighted.
      setSearch('');
      setActiveIndex(Math.max(0, companies.findIndex(company => company.companyId === selectedId)));
    }
    setOpen(next);
  };

  // Keeps the highlighted option in view while the arrow keys walk a list longer than the popup.
  useEffect(() => {
    if (!open || !activeOptionId) return;
    document.getElementById(activeOptionId)?.scrollIntoView({ block: 'nearest' });
  }, [open, activeOptionId]);

  const choose = (company: CompanySummary) => {
    setOpen(false);
    onSelect(company.companyId);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const last = matches.length - 1;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(index => Math.min(Math.min(index, last) + 1, last));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(index => Math.max(Math.min(index, last) - 1, 0));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(Math.max(last, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (active) choose(active);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          id={id}
          data-testid={id}
          data-company-id={selected?.companyId}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby={`${labelId} ${valueId}`}
          disabled={disabled}
          className="flex h-9 w-full min-w-0 items-center gap-2 rounded-lg border border-input bg-card px-3 text-start text-sm text-foreground transition-colors hover:bg-muted focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span id={valueId} className="flex min-w-0 flex-1 items-center gap-2">
            {selected ? (
              <>
                <span className="min-w-0 truncate font-medium">{selected.companyName}</span>
                <span dir="ltr" className="shrink-0 font-mono text-xs text-muted-foreground">#{selected.companyId}</span>
              </>
            ) : (
              <span className="truncate text-muted-foreground">{t('moduleConfig.picker.placeholder')}</span>
            )}
          </span>
          <ChevronsUpDown aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          onOpenAutoFocus={event => {
            event.preventDefault();
            inputRef.current?.focus();
          }}
          className="z-50 w-[min(24rem,calc(100vw-2rem))] rounded-lg border border-border bg-card shadow-lg focus-visible:outline-none"
        >
          <div className="relative border-b border-border p-2">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground ltr:left-4 rtl:right-4"
            />
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={activeOptionId}
              aria-label={t('moduleConfig.picker.searchPlaceholder')}
              placeholder={t('moduleConfig.picker.searchPlaceholder')}
              autoComplete="off"
              spellCheck={false}
              value={search}
              onChange={event => {
                setSearch(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleKeyDown}
              data-testid="module-config-company-search"
              className="h-9 w-full rounded-md border border-input bg-card pe-3 ps-8 text-sm text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
            />
          </div>
          <ul
            id={listId}
            role="listbox"
            aria-labelledby={labelId}
            data-testid="module-config-company-list"
            className="max-h-64 overflow-y-auto p-1"
          >
            {matches.map((company, index) => {
              const isSelected = company.companyId === selectedId;
              const isActive = active?.companyId === company.companyId;
              return (
                <li
                  key={company.companyId}
                  id={optionId(company.companyId)}
                  role="option"
                  aria-selected={isSelected}
                  data-testid={`module-config-company-option-${company.companyId}`}
                  onMouseMove={() => setActiveIndex(index)}
                  onClick={() => choose(company)}
                  className={`flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm ${
                    isActive ? 'bg-muted text-foreground' : 'text-foreground'
                  }`}
                >
                  <Check
                    aria-hidden="true"
                    className={`h-4 w-4 shrink-0 text-primary ${isSelected ? '' : 'invisible'}`}
                  />
                  <span className="min-w-0 flex-1 truncate">{company.companyName}</span>
                  <span dir="ltr" className="shrink-0 font-mono text-xs text-muted-foreground">#{company.companyId}</span>
                </li>
              );
            })}
            {matches.length === 0 && (
              <li role="presentation" className="px-3 py-6 text-center text-sm text-muted-foreground">
                {t('moduleConfig.picker.noMatch')}
              </li>
            )}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
